import { atom, type ReadableAtom } from 'nanostores';

import { definePatientScoped } from '@/shared/lib/patient-scope';

/**
 * Registry mapping each overlay id to the payload its component requires.
 *
 * This interface is intentionally *open*. The concrete payload shapes live in
 * the feature layer, and ``shared`` must not depend upwards on features — so
 * instead of importing those types here, each feature contributes its own
 * ``id → payload`` entry by augmenting this interface via declaration merging:
 *
 * ```ts
 * // in some feature's model file
 * declare module '@/shared/stores/ui/copilotOverlay' {
 *   interface CopilotOverlayPayloads {
 *     dosespotAction: DosespotActionPrefill;
 *   }
 * }
 * ```
 *
 * Overlays that own no feature payload are declared here directly as ``null``.
 *
 * Everything else — the id union, the stored entry, and ``open`` — derives
 * from this single source of truth, so an id can never be paired with the
 * wrong payload.
 */
export interface CopilotOverlayPayloads {
  followUps: null;
  aiDebug: null;
}

export type CopilotOverlayId = keyof CopilotOverlayPayloads;

/**
 * A live overlay instance.
 *
 * This is a discriminated union over ``id``, so narrowing on ``entry.id``
 * reveals the concrete ``payload`` type for that overlay.
 *
 * ``openId`` is a monotonic token bumped on every ``open`` so the outlet can
 * remount an overlay's content on each fresh open.
 */
export type CopilotOverlayEntry = {
  [K in CopilotOverlayId]: { id: K; payload: CopilotOverlayPayloads[K]; openId: number };
}[CopilotOverlayId];

/** The overlays open for one patient. */
export interface CopilotOverlayStore {
  /** Open overlays, back to front: the last entry is on top. */
  readonly $overlays: ReadableAtom<CopilotOverlayEntry[]>;
  /**
   * Open (or re-open) an overlay, carrying its payload.
   *
   * The payload type is checked against ``id`` through ``CopilotOverlayPayloads``,
   * so e.g. ``open('dosespotAction', aLabPrefill)`` is a type error. Re-opening
   * an already-open overlay moves it to the front and remounts its content with
   * the new payload.
   */
  readonly open: <K extends CopilotOverlayId>(id: K, payload: CopilotOverlayPayloads[K]) => void;
  /** Close the overlay with ``id``, or the top overlay when ``id`` is omitted. */
  readonly close: (id?: CopilotOverlayId) => void;
}

export function createCopilotOverlayStore(): CopilotOverlayStore {
  const $overlays = atom<CopilotOverlayEntry[]>([]);
  let nextOpenId = 0;

  return {
    $overlays,
    open: (id, payload) => {
      nextOpenId += 1;

      // TypeScript can't prove that this value is a CopilotOverlayEntry, but by construction
      // (see above) id and payload are matched -- so, this cast is safe
      const entry = { id, payload, openId: nextOpenId } as CopilotOverlayEntry;
      const withoutId = $overlays.get().filter((overlay) => overlay.id !== id);
      $overlays.set([...withoutId, entry]);
    },
    close: (id) => {
      const current = $overlays.get();
      if (id !== undefined) {
        $overlays.set(current.filter((overlay) => overlay.id !== id));
        return;
      }
      if (current.length === 0) {
        return;
      }
      $overlays.set(current.slice(0, -1));
    },
  };
}

/**
 * The current patient's overlays. Each patient scope has its own, so overlays
 * never carry over to another patient. Must be called inside a
 * ``PatientScopeProvider``.
 */
export const useCopilotOverlays = definePatientScoped(createCopilotOverlayStore);
